import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { uniqBy } from 'lodash';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import ROUTE from '../../constants/route-constants';
import { DATA_RETRIEVAL_INTERVAL, TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components

import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';

import DatePickerOne from '../../components/Forms/DatePicker/DatePickerOne';

import SelectGroupOne from '../../components/Forms/SelectGroup/SelectGroupOne';
import Switcher, { ResponseSwitch } from '../../components/Switcher/Switcher';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import SwitcherOne from '../../components/Switchers/SwitcherOne';
import DraftMetaFields from '../../components/DraftMetaFields/DraftMetaFields';

type CreateContractProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const CreateContract: React.FC<CreateContractProps> = ({
  createContract,
  locations,
  getLocations,
  organization,
  getOrganization,
  getLocationMetaFields,
}) => {
  const navigate = useNavigate();

  const [options, setOptions] = useState([
    { value: '', label: 'No client selected', disabled: false },
  ]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: '',
      start_at: null,
      end_at: null,
      locationId: null,
      endless: false,
      isCelsius: organization?.isCelsius ?? false,
      metaFields: [] as { key: string; value: string }[],
    },
    validationSchema: object({
      name: string()
        .required('Required')
        .min(
          TEXT_LENGTH_LIMITS.MIN,
          `Min name length is ${TEXT_LENGTH_LIMITS.MIN} characters`
        )
        .max(
          TEXT_LENGTH_LIMITS.MAX,
          `Max  name length is ${TEXT_LENGTH_LIMITS.MAX} characters`
        ),
      start_at: string()
        .required('Required')
        .transform((value) => (value === '' ? null : value))
        .typeError('Must be a valid timestamp')
        .test(
          'date_must_be_now_or_earlier',
          'Start date must be the current time or earlier',
          function (value) {
            if (!value) return true;

            const date = new Date(value);
            const now = new Date();

            return date < now;
          }
        ),
      end_at: string()
        .nullable()
        .transform(value => (value === '' ? null : value))
        .when('endless', {
          is: false,
          then: (schema) =>
            schema
              .required('End date is required when contract is not endless')
              .test(
                'is-at-least-1-hour-after-start',
                'End date must be at least 1 hour after start date',
                function (endValue) {
                  const { start_at } = this.parent;

                  if (!endValue || !start_at) return true;

                  const end = new Date(endValue).getTime();
                  const start = new Date(start_at).getTime();

                  return end >= start + DATA_RETRIEVAL_INTERVAL.ONE_HOUR;
                }
              ),
          otherwise: (schema) => schema.nullable(),
        }),
    }),
    onSubmit: async (values) => {
      await createContract(values);
      navigate(ROUTE.CONTRACTS);
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!locations?.length) return;

    const options = locations.map((location) => {
      return {
        value: location.id,
        label: location.name,
        disabled: false,
      };
    });

    setOptions((prev) => uniqBy([...prev, ...options], 'value'));
  }, [locations?.length]);

  useEffect(() => {
    if (organization && !formik.values.locationId) {
      formik.setFieldValue('isCelsius', organization.isCelsius);
    }
  }, [organization]);

  useEffect(() => {
    if (formik.values.locationId && locations) {
      const selectedLocation = locations.find((loc) => loc.id === formik.values.locationId);
      if (selectedLocation) {
        formik.setFieldValue('isCelsius', selectedLocation.isCelsius);
      }
    }
  }, [formik.values.locationId, locations]);

  useEffect(() => {
    const fetchLocationMetaFields = async () => {
      if (!formik.values.locationId) {
        formik.setFieldValue('metaFields', []);
        return;
      }

      const fetchedFields = await getLocationMetaFields(formik.values.locationId);
      formik.setFieldValue('metaFields', fetchedFields.map((field) => ({ key: field.key, value: field.value })));
    };

    fetchLocationMetaFields();
  }, [formik.values.locationId]);

  const disabledSubmit = Object.keys(formik.errors).length > 0 || Object.keys(formik.touched).length === 0

  const fetchData = async () => {
    await Promise.all([getLocations(), getOrganization()]);
    setIsDataLoaded(true);
  };

  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    formik.handleChange({
      target: { name, value },
    });
  };

  const handleDateChange = async (name, value) => {
    await formik.setFieldValue(name, value);
    await formik.setFieldTouched(name, true);
  };

  const handleSwitcherChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    formik.handleChange(event);

    if (!formik.touched[name]) {
      await formik.setFieldTouched(name, true);
    }

    if (name === 'endless') {
      if (checked) {
        const filteredErrors = Object.fromEntries(Object.entries(formik.errors).filter(([key]) => key !== 'end_at'));
        formik.setErrors(filteredErrors);
      } else {
        await formik.validateField('end_at');
      }
    }
  };

  const onUnitsChange = (data: ResponseSwitch) => {
    formik.setFieldValue('isCelsius', data.value);
    formik.setFieldTouched('isCelsius', true);
  };

  return (
    <>
      {!isDataLoaded && (
        <div className="h-full w-full flex items-center justify-center">
          <SpinnerLoader />
        </div>
      )}
      {isDataLoaded && (
        <div className="p-create-contract pb-4 md:pb-25">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-medium text-black dark:text-white">
                Create Contract
              </h3>
            </div>
            <form
              className="flex flex-col gap-5.5 p-7"
              onSubmit={formik.handleSubmit}
            >
              <div className="w-full">
                <InputControl
                  label="Name"
                  type="text"
                  name="name"
                  placeholder="Enter your contract name"
                  value={formik.values.name}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={formik.touched.name && formik.errors.name}
                  errorText={formik.errors.name}
                />
              </div>

              <DatePickerOne
                placeholder="Select Start Date"
                name="start_at"
                value={formik.values.start_at ?? ''}
                label="Start Date"
                handleChange={handleDateChange}
                showError={(formik.touched.start_at && formik.errors.start_at) ? true : false}
                errorText={`${formik.errors.start_at}`}
              />

              {!formik.values.endless && (
                <DatePickerOne
                  placeholder="Select End Date"
                  name="end_at"
                  value={formik.values.end_at ?? ''}
                  label="End Date"
                  handleChange={handleDateChange}
                  showError={(formik.touched.end_at && formik.errors.end_at) ? true : false}
                  errorText={`${formik.errors.end_at}`}
                />
              )}

              <SwitcherOne
                name="endless"
                label="Endless"
                enabled={formik.values.endless}
                handleChange={handleSwitcherChange}
              />

              <div className="w-full">
                <SelectGroupOne
                  label="Client"
                  handleChange={handleSelect}
                  options={options}
                  selectedOption={formik.values.locationId ?? ''}
                  name="locationId"
                />
              </div>

              <div className="c-input-control__input-wrapper -mt-8">
                <p className="py-2.5 font-medium text-black dark:text-white">Temperature Units</p>
                <Switcher
                  options={{
                    left: CycleMeasurementSymbol.Celsius,
                    right: CycleMeasurementSymbol.Fahrenheit,
                  }}
                  isEnable={formik.values.isCelsius}
                  onChange={onUnitsChange}
                  switcherId="contract-units-id"
                />
              </div>

              <div className="flex justify-end gap-4.5">
                <InteractiveButton
                  text="Create"
                  submit
                  disabled={disabledSubmit}
                  isLoading={formik.isSubmitting}
                />
              </div>
            </form>
          </div>
          {formik.values.locationId && (
            <DraftMetaFields
              metaFields={formik.values.metaFields}
              onChange={(list) => formik.setFieldValue('metaFields', list)}
              title="Contract meta fields"
            />
          )}
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  locations: state.locations.locations,
  organization: state.organizations.organization,
});

const mapDispatch = (dispatch: Dispatch) => ({
  createContract: dispatch.contracts.createContract,
  getLocations: dispatch.locations.getLocations,
  getOrganization: dispatch.organizations.getOrganization,
  getLocationMetaFields: dispatch.location_meta_fields.getMetaFields,
});

export default connect(mapState, mapDispatch)(CreateContract);
